<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { UserProfileUpdateDTO } from '@/types'

import PersonalCalendarFeedCard from '@/components/user/PersonalCalendarFeedCard.vue'
import { useAppToast } from '@/composables/useAppToast'
import userService from '@/services/userService'

type NotificationLanguagePreference = 'combined' | 'german' | 'english'
type ProfileUpdateSource = {
  firstName?: string | null
  lastName?: string | null
  mail?: string
  department?: string | null
  plannedProductId?: string | null
  plannedProductName?: string | null
  externalProduct?: string | null
  matriculationId?: string | null
}

const toast = useAppToast()
const { t } = useI18n()
const { userInfo, fetchUserInfo } = useUser()

const loading = ref(false)
const selectedPreference = ref<NotificationLanguagePreference>('combined')

const preferenceOptions = computed(() => [
  {
    label: t('pages.user.settings.preferences.options.combined'),
    value: 'combined' as const,
  },
  {
    label: t('common.labels.german'),
    value: 'german' as const,
  },
  {
    label: t('common.labels.english'),
    value: 'english' as const,
  },
])

const activePreferenceLabel = computed(() => {
  return (
    preferenceOptions.value.find((option) => option.value === selectedPreference.value)?.label ??
    selectedPreference.value
  )
})

const hasChanges = computed(
  () => selectedPreference.value !== (userInfo.value?.notificationLanguagePreference ?? 'combined'),
)

watch(
  () => userInfo.value?.notificationLanguagePreference,
  (newPreference) => {
    selectedPreference.value = (newPreference ?? 'combined') as NotificationLanguagePreference
  },
  { immediate: true },
)

function buildProfileUpdatePayload(profile: ProfileUpdateSource): UserProfileUpdateDTO {
  return {
    firstName: profile.firstName ?? null,
    lastName: profile.lastName ?? null,
    email: profile.mail ?? null,
    department: profile.department ?? null,
    plannedProductId: profile.plannedProductId ?? null,
    plannedProductName: profile.plannedProductName ?? null,
    externalProduct: profile.externalProduct ?? null,
    matriculationId: profile.matriculationId ?? null,
    notificationLanguagePreference: selectedPreference.value,
  }
}

function resetPreferences() {
  selectedPreference.value = (userInfo.value?.notificationLanguagePreference ??
    'combined') as NotificationLanguagePreference
}

async function savePreferences() {
  if (!userInfo.value || !hasChanges.value) {
    return
  }

  loading.value = true
  try {
    await userService.updateUserProfile(buildProfileUpdatePayload(userInfo.value))
    await fetchUserInfo(true)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.user.settings.preferences.messages.saveSuccess'),
      duration: 3000,
    })
  } catch (error: any) {
    console.error('Failed to update notification language preference:', error)
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.title ||
      t('pages.user.settings.preferences.messages.saveError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorMessage,
      duration: 5000,
    })
  } finally {
    loading.value = false
  }
}

definePage({
  meta: {
    requiresAuth: true,
  },
})
</script>

<template>
  <div class="flex flex-col gap-4">
    <UCard>
      <template #header>
        <div class="flex items-center justify-between gap-3">
          <span>{{ t('pages.user.settings.preferences.title') }}</span>
          <AppSpinner v-if="loading" style="width: 30px; height: 30px" />
        </div>
      </template>
      <template #default>
        <div class="max-w-180">
          <p class="mb-6">
            {{ t('pages.user.settings.preferences.description') }}
          </p>

          <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
            <div class="rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
              <div class="mb-4">
                <h2 id="preferences-language-label" class="text-lg font-semibold">
                  {{ t('pages.user.settings.preferences.language.title') }}
                </h2>
                <p class="mt-1 text-sm text-muted">
                  {{ t('pages.user.settings.preferences.language.description') }}
                </p>
              </div>

              <AppSegmentedControl
                v-model="selectedPreference"
                :options="preferenceOptions"
                option-label="label"
                option-value="value"
                :allow-empty="false"
                class="w-full"
                :disabled="loading"
                aria-labelledby="preferences-language-label"
              />
            </div>

            <div
              class="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800/50"
            >
              <p class="text-sm font-semibold">
                {{ t('pages.user.settings.preferences.preview.title') }}
              </p>
              <p class="mt-2 text-sm text-muted">
                {{ t('pages.user.settings.preferences.preview.description') }}
              </p>

              <div
                class="mt-4 flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-2 dark:bg-neutral-800"
              >
                <UIcon name="i-lucide-mail" class="text-sm text-[var(--app-text-primary)]" />
                <span class="text-sm font-medium">{{ activePreferenceLabel }}</span>
              </div>

              <p class="mt-3 text-xs text-muted">
                {{ t('pages.user.settings.preferences.preview.hint') }}
              </p>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex max-w-180 justify-end gap-2">
          <UButton
            :label="t('common.actions.cancel')"
            color="neutral"
            :disabled="loading || !hasChanges"
            @click="resetPreferences"
          />
          <UButton
            :label="t('common.actions.save')"
            icon="i-lucide-check"
            :disabled="!hasChanges"
            :loading="loading"
            @click="savePreferences"
          />
        </div>
      </template>
    </UCard>

    <PersonalCalendarFeedCard />
  </div>
</template>
